import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Inbox, Database, Workflow, CheckCircle2, Settings, ShieldCheck, FileCheck } from 'lucide-react';

const OpsIntelligenceSuite = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-[#060D1B] via-[#0A1628] to-[#0d1f3c]">
        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="text-center">
            <div className="inline-block px-3 py-1 bg-[#1A56DB]/20 text-[#1A56DB] text-xs font-semibold uppercase tracking-wider rounded-full mb-4">
              Operations Layer
            </div>
            <h1 className="text-h1 text-white mb-6">Ops Intelligence Suite</h1>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto mb-8">
              Six critical banking operations — running on manual handoffs — replaced by configurable AI agents governed by your Business Rules Engine.
            </p>
            <Button 
              asChild 
              size="lg" 
              className="bg-[#1A56DB] hover:bg-[#1A56DB]/90 text-white font-semibold text-lg px-8 h-14 btn-hover-lift shadow-lg shadow-[#1A56DB]/30"
            >
              <Link to="/contact">Request Demo</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="section-padding px-6 bg-[#EFF6FF]">
        <div className="max-w-[1200px] mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-h3 text-[#0A1628] mb-4">Workflow Automation</h2>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto">
              End-to-end orchestration across the credit operations lifecycle
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-10">
            <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="p-3 bg-[#1A56DB]/10 rounded-lg w-fit mb-3">
                  <Inbox className="text-[#1A56DB]" size={24} />
                </div>
                <CardTitle className="text-[#0A1628] text-xl">Intake Automation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#6B7280] text-sm mb-4">
                  Automated mailbox triage, document processing and dossier creation
                </p>
                <ul className="space-y-2 text-sm text-[#374151]">
                  {['Intent Classification & Routing', 'Automated Document Parsing', 'Checklist Validation', 'LOS Handoff Automation'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-[#1A56DB]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="p-3 bg-[#1A56DB]/10 rounded-lg w-fit mb-3">
                  <Database className="text-[#1A56DB]" size={24} />
                </div>
                <CardTitle className="text-[#0A1628] text-xl">Financial Analysis Workflows</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#6B7280] text-sm mb-4">
                  Automated spreads, cashflow analysis and risk signal generation
                </p>
                <ul className="space-y-2 text-sm text-[#374151]">
                  {['Bank Statement Analysis', 'GST & ITR Processing', 'Cashflow Stability Assessment', 'Anomaly Detection'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-[#1A56DB]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="p-3 bg-[#1A56DB]/10 rounded-lg w-fit mb-3">
                  <Workflow className="text-[#1A56DB]" size={24} />
                </div>
                <CardTitle className="text-[#0A1628] text-xl">Credit Operations</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#6B7280] text-sm mb-4">
                  End-to-end workflow orchestration for secured lending
                </p>
                <ul className="space-y-2 text-sm text-[#374151]">
                  {['KYC & PD Coordination', 'Legal & Valuation Processing', 'Policy Compliance Checks', 'Disbursal Verification'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-[#1A56DB]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Credit Lifecycle */}
          <div className="bg-[#0A1628] rounded-xl p-8">
            <h3 className="text-h3 text-white mb-6 text-center">Credit Lifecycle Orchestration</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
              {['Intake', 'KYC & PD', 'Legal & Valuation', 'Underwrite', 'Decision', 'Disbursal', 'Monitoring'].map((step, index) => (
                <div key={step} className="relative">
                  {index < 6 && (
                    <>
                      <div className="absolute left-4 top-full h-4 w-px bg-white/20 sm:hidden" />
                      <div className="hidden xl:block absolute left-full top-1/2 h-px w-4 -translate-y-1/2 bg-white/20" />
                    </>
                  )}

                  <div className="glass-card flex items-center gap-3 px-5 py-4 text-white min-h-[72px] relative">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#1A56DB] to-[#0E7490] text-white text-sm font-semibold shadow-md shadow-[#1A56DB]/30 flex-shrink-0">
                      {index + 1}
                    </div>
                    <span className="text-sm font-medium leading-snug">{step}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Business Rules Engine Section */}
      <section className="section-padding px-6 bg-white">
        <div className="max-w-[1200px] mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-h3 text-[#0A1628] mb-4">Governed by Your Business Rules Engine</h2>
            <p className="text-body-lg text-[#6B7280] max-w-4xl mx-auto">
              Unlike hardcoded automation, every workflow in the Ops Intelligence Suite is governed by Cognifin's Business Rules & Policy Engine — Tier 2 of the platform architecture. Credit policies, compliance mandates, and routing logic are configurable in real time, without engineering support.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-[#EFF6FF] border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="p-3 bg-[#4F46E5]/10 rounded-lg w-fit mb-4">
                  <Settings className="text-[#4F46E5]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">Policy Configurability</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  Update credit criteria, thresholds, and routing rules in a no-code interface.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#EFF6FF] border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="p-3 bg-[#4F46E5]/10 rounded-lg w-fit mb-4">
                  <ShieldCheck className="text-[#4F46E5]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">Real-Time Compliance</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  Mandates and regulatory constraints enforced at every workflow step, automatically.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#EFF6FF] border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="p-3 bg-[#4F46E5]/10 rounded-lg w-fit mb-4">
                  <FileCheck className="text-[#4F46E5]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">Full Audit Trail</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  Every rule applied to every document is logged and reviewable. Built for internal audit and regulatory review.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding px-6 bg-gradient-to-r from-[#0A1628] to-[#1A56DB]">
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-h2 text-white mb-6">
            Automate Your Credit Operations
          </h2>
          <p className="text-body-lg text-white/80 mb-8 max-w-2xl mx-auto">
            See how Ops Intelligence Suite can transform your operational efficiency
          </p>
          <Button 
            asChild 
            size="lg" 
            className="bg-white hover:bg-white/90 text-[#0A1628] font-bold text-lg px-10 h-14 btn-hover-lift shadow-xl"
          >
            <Link to="/contact">Request a Demo</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default OpsIntelligenceSuite;
